import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Dialog from 'components/styleguide/dialog/Dialog';
import Hint from 'components/styleguide/hint/Hint';
import IconButton from 'components/styleguide/icon_button/IconButton';
import LoadingSpinner from 'components/styleguide/loading_spinner/LoadingSpinner';
import Radio from 'components/styleguide/radio/Radio';
import { getCSRFToken } from 'lib/request';

const styles = require('./DocumentUploader.module.scss');

export type DefaultDocument = {
  id: string;
  name: string;
  size?: number;
  standard?: string;
  subtitle?: string;
  uploaded_at?: string;
};

type DocumentUploaderProps = {
  accept?: string;
  deleteUrl?: string;
  error?: string;
  formName?: string;
  hint?: string;
  id?: string;
  isDocumentSelected?: boolean;
  isLoading?: boolean;
  isMultiple?: boolean;
  loadingTitle?: string;
  name?: string;
  onDelete?: (document: DefaultDocument) => void;
  onDocumentSelect?: (document: DefaultDocument) => void;
  onFileUpload?: (file: File) => void;
  onPreview?: () => void;
  previewUrl?: string;
  replaceable?: boolean;
  required?: boolean;
  subtitle?: string;
  title?: string;
  uploadedDocument?: DefaultDocument | null;
};

const MAX_FILE_SIZE = 10 * 1024 * 1024;

function useDragDrop(onDrop: (e: React.DragEvent) => void) {
  const [isDragging, setIsDragging] = useState(false);
  const counter = useRef(0);

  return {
    isDragging,
    dragHandlers: {
      onDragOver: (e: React.DragEvent) => e.preventDefault(),
      onDragEnter: (e: React.DragEvent) => { e.preventDefault(); counter.current++; setIsDragging(true); },
      onDragLeave: (e: React.DragEvent) => { e.preventDefault(); counter.current--; if (counter.current === 0) setIsDragging(false); },
      onDrop: (e: React.DragEvent) => { counter.current = 0; setIsDragging(false); onDrop(e); },
    },
  };
}

function isValidFileType(file: File, accept?: string): boolean {
  if (!accept) return true;
  const mimeType = file.type;
  return accept.split(',').some((pattern) => {
    const type = pattern.trim().toLowerCase();
    if (type.startsWith('.')) return file.name.toLowerCase().endsWith(type);
    if (type.endsWith('/*')) return mimeType.startsWith(type.split('/*')[0] + '/');
    if (type.includes('/')) return mimeType === type;
    return (file.name.split('.').pop()?.toLowerCase() || '') === type;
  });
}

export default function DocumentUploader({
  accept,
  deleteUrl,
  error,
  formName,
  hint,
  id,
  isDocumentSelected,
  isLoading,
  isMultiple,
  loadingTitle,
  name,
  onDelete,
  onDocumentSelect,
  onFileUpload,
  onPreview,
  previewUrl,
  replaceable,
  required,
  subtitle,
  title,
  uploadedDocument,
}: DocumentUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalError, setInternalError] = useState(error || '');
  const [selectedFile, setSelectedFile] = useState<DefaultDocument | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    setInternalError(error || '');
  }, [error]);

  const isFormMode = !!name;
  const currentDocument = (isFormMode ? selectedFile : null) || uploadedDocument;
  const displayError = internalError;
  const isFilled = !!currentDocument && !isLoading;

  const hasMessage = Boolean(displayError) || Boolean(hint);
  const messageId = hasMessage ? `${id || 'doc-uploader'}-message` : undefined;

  const openFilePicker = () => inputRef.current?.click();

  const processFile = (file: File, resetInput?: () => void) => {
    setInternalError('');
    if (isValidFileType(file, accept) && file.size <= MAX_FILE_SIZE) {
      if (isFormMode) {
        setSelectedFile({ id: '', name: file.name, size: file.size });
      } else {
        onFileUpload?.(file);
      }
    } else {
      resetInput?.();
      setInternalError(
        currentDocument
          ? 'To replace, please select a valid format and size document'
          : 'Please select a valid format and size document'
      );
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file, () => { e.target.value = ''; });
    if (!isFormMode) e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    if (isFormMode && inputRef.current) {
      const dt = new DataTransfer();
      dt.items.add(file);
      inputRef.current.files = dt.files;
    }
    processFile(file, () => { if (inputRef.current) inputRef.current.value = ''; });
  };

  const handleClick = () => {
    if (isFilled && isMultiple && currentDocument) {
      onDocumentSelect?.(currentDocument);
    } else if (onFileUpload || isFormMode) {
      openFilePicker();
    }
  };

  const handleConfirmDelete = async () => {
    if (!currentDocument) return;
    try {
      if (deleteUrl) {
        const response = await fetch(deleteUrl, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCSRFToken() || '',
            Accept: 'application/json',
          },
        });
        if (!response.ok) return;
        const result = await response.json();
        if (!result.success) return;
      }
      onDelete?.(currentDocument);
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  const { isDragging, dragHandlers } = useDragDrop(handleDrop);
  const showDropOverlay = isDragging && !(isFilled && isMultiple);

  const dropzoneClass = classNames(
    styles['ui-document-uploader__dropzone'],
    {
      [styles['ui-document-uploader__dropzone--filled']]: isFilled && !isMultiple && !showDropOverlay,
      [styles['ui-document-uploader__dropzone--row']]: isFilled && isMultiple && !showDropOverlay,
      [styles['ui-document-uploader__dropzone--selected']]: isFilled && isMultiple && isDocumentSelected,
      [styles['ui-document-uploader__dropzone--error']]: !!displayError,
      [styles['ui-document-uploader__dropzone--dragging']]: showDropOverlay,
    },
  );

  return (
    <div className={styles['ui-document-uploader']} aria-busy={isLoading}>
      {isLoading && (
        <div className={styles['ui-document-uploader__loading']} role="status">
          <LoadingSpinner size="small-medium" context='talent'/>
          <div className={styles['ui-document-uploader__file-info']}>
            <span className={styles['ui-document-uploader__title']}>
              {loadingTitle || 'Uploading your document...'}
            </span>
          </div>
        </div>
      )}

      {!isLoading && (
        <div
          className={dropzoneClass}
          {...(isMultiple ? {} : dragHandlers)}
          onClick={handleClick}
          role="button"
          tabIndex={0}
          aria-describedby={messageId}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              handleClick();
            }
          }}
          aria-label={
            showDropOverlay ? 'Drop file to upload'
            : (isFilled && currentDocument) ? `${isMultiple ? 'Select' : 'Uploaded:'} ${currentDocument.name}`
            : 'Upload document'
          }
          aria-pressed={isMultiple && isFilled ? isDocumentSelected : undefined}
        >
          <div className={styles['ui-document-uploader__content']}>
            {showDropOverlay ? (
              <div className={styles['ui-document-uploader__file-info']}>
                <span className={styles['ui-document-uploader__drop-text']}>Drop it here</span>
              </div>
            ) : !isFilled ? (
              <div className={styles['ui-document-uploader__file-info']}>
                <div className={styles['ui-document-uploader__upload-text']}>
                  <span className={styles['ui-document-uploader__upload-link']}>{title || 'Click to upload'}</span>
                  <span className={styles['ui-document-uploader__upload-body']}>or drop it here</span>
                </div>
                {subtitle && <span className={styles['ui-document-uploader__subtitle']}>{subtitle}</span>}
              </div>
            ) : currentDocument && (
              <>
                {isMultiple && (
                  <Radio
                    name={formName}
                    value={currentDocument.id}
                    checked={isDocumentSelected}
                    context="talent"
                    onChange={() => onDocumentSelect?.(currentDocument)}
                  />
                )}
                <div className={classNames(
                  styles['ui-document-uploader__file-info'],
                  styles['ui-document-uploader__file-info--has-file'],
                )}>
                  <span className={styles['ui-document-uploader__title']}>{currentDocument.name}</span>
                  {currentDocument.subtitle && (
                    <span className={styles['ui-document-uploader__subtitle']}>
                      {currentDocument.subtitle}
                    </span>
                  )}
                </div>
                <div className={styles['ui-document-uploader__actions']}>
                  {(previewUrl || onPreview) && (
                    <IconButton
                      type="terceary"
                      context='talent'
                      size='M'
                      iconName="eye"
                      aria-label={`Preview ${currentDocument.name}`}
                      {...(previewUrl
                        ? { href: previewUrl, target: '_blank', rel: 'noopener noreferrer', onClick: (e) => e.stopPropagation() }
                        : { onClick: (e) => { e.stopPropagation(); onPreview?.(); } })}
                    />
                  )}
                  {(!isMultiple || onFileUpload || replaceable) && (
                    <IconButton
                      type="terceary"
                      context='talent'
                      size='M'
                      iconName="repeat"
                      aria-label="Replace document"
                      onClick={(e) => { e.stopPropagation(); openFilePicker(); }}
                    />
                  )}
                  {(onDelete || deleteUrl) && (
                    <IconButton
                      type="terceary"
                      context='talent'
                      size='M'
                      iconName="trash"
                      aria-label={`Delete ${currentDocument.name}`}
                      onClick={(e) => { e.stopPropagation(); setIsDeleteModalOpen(true); }}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {hasMessage && (
        <Hint
          id={messageId}
          variant={displayError ? 'error' : 'neutral'}
          message={displayError || hint}
        />
      )}

      <input
        ref={inputRef}
        type="file"
        id={id}
        name={name}
        required={required}
        onChange={handleFileChange}
        className={styles['ui-document-uploader__hidden-input']}
        accept={accept}
        aria-label="Choose file to upload"
      />

      {(onDelete || deleteUrl) && (
        <Dialog
          isOpen={isDeleteModalOpen}
          title="Delete resume from profile?"
          message="Upon deleting, this resume will no longer be available on your profile."
          context="talent"
          onConfirm={handleConfirmDelete}
          onClose={() => setIsDeleteModalOpen(false)}
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}
    </div>
  );
}
